/**
 * Created by Ning Jiang on 2021-06-29.
 */
import {ContinueScreen} from "../../core/screen/ContinueScreen";
import {Layers, TombstoneRipLayers} from "../view/TombstoneRipLayers";
import {OpenSans} from "@nolimitcity/slot-launcher/bin/loader/font/OpenSans";
import {FontWeight} from "@nolimitcity/slot-launcher/bin/loader/font/FontStatics";
import {SlotGame} from "@nolimitcity/slot-game/bin/SlotGame";
import {TombstoneRip} from "../TombstoneRip";
import {GameMode} from "@nolimitcity/slot-game/bin/flowcontrol/GameMode";
import {Translation} from "../../core/utils/Translation";
import {MultiSprites} from "@nolimitcity/slot-game/bin/animation/MultiSprites";
import {BigWinCountUp} from "../winpresentation/presentations/bigwin/BigWinCountUp";
import {gsap, TimelineLite} from "gsap";
import {WinCapParticles} from "./WinCapParticles";
import {TombstoneRipSound} from "../sound/TombstoneRipSound";
import {BelowStakeWinRestriction} from "../server/BelowStakeWinRestriction";

export class WinCapScreen extends ContinueScreen {

    private _levelDuration:number = 6;
    private _fadeInDuration:number = 2;

    private _bg:PIXI.Sprite;
    private _slide:MultiSprites;
    private _winX:PIXI.Sprite;
    private _played:PIXI.Text;
    private _countUp:BigWinCountUp;
    private _particles:WinCapParticles;

    private _isPumping:boolean;
    private _pumpTl:TimelineLite | null;

    constructor() {
        super(TombstoneRipLayers.getLayer(Layers.SCREENS)!);

        this._bg = new PIXI.Sprite(TombstoneRip.assetLoader.getTextures("BACKGROUND_wincap")[0]);
        this._bg.anchor.set(0.5);
        this._bg.position.set(360, 360);
        this._container.addChild(this._bg);

        this._slide = new MultiSprites();
        for(let i = 1; i <= 4; i++) {
            this._slide.addSprites({
                name : `level${i}`,
                textures : TombstoneRip.assetLoader.getTextures(`wincap/Win_cap${i}${!!SlotGame.apiPlugin.gameClientConfiguration.useCensoredGfxUS && i === 3 ? "_WV" : ""}`),
                anchor : new PIXI.Point(0.5, 0.5)
            });
            this._slide.sprites[`level${i}`].rotation = (Math.PI / 30) * (Math.random() * 2 - 1);
        }
        this._slide.position.set(360, 360);
        this._container.addChild(this._slide);

        this._winX = new PIXI.Sprite(TombstoneRip.assetLoader.getTextures("wincap/300000x")[0]);
        this._winX.anchor.set(0.5, 0.5);
        this._winX.position.set(360, 340);
        this._container.addChild(this._winX);

        this._played = new PIXI.Text("", new PIXI.TextStyle({
            fill : 0xffffff,
            fontFamily : OpenSans.FAMILY,
            wordWrap : false,
            fontWeight : FontWeight.BOLD,
            fontSize : 30
        }));
        this._played.anchor.set(0.5, 0.5);
        this._played.position.set(360, 560);
        this._container.addChild(this._played);

        this._particles = new WinCapParticles();
        this._particles.position.set(360, 360);
        this._container.addChild(this._particles);

        this._countUp = new BigWinCountUp(TombstoneRipLayers.getLayer(Layers.SCREENS)!, 120);
    }

    public show(onContinueCallBack:() => void) {
        this.reset();
        const data = TombstoneRip.model.parsedGameData;
        const bet = data.playedBetValue;
        const spinPlayed = TombstoneRip.model.parsedGameData.numberOfFreespinsPlayed;
        this._played.text = Translation.translate("Spins played: ${spinPlayed}").replace("${spinPlayed}", spinPlayed.toString());
        SlotGame.keypadPlugin.hide();
        const tl = new TimelineLite();
        tl.add(() => {
            this.visible = true;
            this._particles.play();
            TombstoneRipSound.soundPlayer.playTrack({name : TombstoneRipSound.WIN_CAP_COUNT_UP});
            TombstoneRipSound.fadeMusic(0, 300);
        });
        tl.add([
            this._countUp.getCountUpAnimation(0, bet * 25000, this._levelDuration),
            gsap.fromTo(this._countUp.scale, {x : 1, y : 1}, {x : 1.2, y : 1.2, duration : this._levelDuration}),
            () => this.playSlide(1)
        ]);
        tl.add([
            this._countUp.getCountUpAnimation(bet * 25000, bet * 100000, this._levelDuration),
            gsap.fromTo(this._countUp.scale, {x : 1, y : 1}, {x : 1.2, y : 1.2, duration : this._levelDuration}),
            () => this.playSlide(2)
        ]);
        tl.add([
            this._countUp.getCountUpAnimation(bet * 100000, data.totalWin, this._levelDuration),
            gsap.fromTo(this._countUp.scale, {x : 1, y : 1}, {x : 1.2, y : 1.2, duration : this._levelDuration}),
            () => this.playSlide(3)
        ]);
        tl.add([
            () => this.playSlide(4),
            () => {
                TombstoneRipSound.soundPlayer.playTrack({
                    name : TombstoneRipSound.WIN_CAP,
                    volume : 1,
                    repeat : 100
                });
                TombstoneRipSound.fadeMusic(0, 1);
            },
            gsap.to(this._countUp, {alpha : 0, duration : this._fadeInDuration})
        ]);
        tl.add(() => {
            SlotGame.keypadPlugin.setWin(data.totalWin, true, BelowStakeWinRestriction.isWinBelowStake());
            this._winX.visible = true;
            this.playPump();
            this._played.visible = data.mode !== GameMode.NORMAL;
            TombstoneRip.background.updateEnvironment(TombstoneRip.model.currentParsedData.nextMode);
            TombstoneRip.reels.forEach(reel => {
                reel.resetSuperMidSymbol();
            });
            TombstoneRip.superFsPop.reset();
            super.show(onContinueCallBack);
        });
    }

    private playSlide(level:number):void {
        this._slide.playSpriteFromTo(`level${level}`,
            {alpha : level === 1 ? 1 : 0},
            {duration : this._fadeInDuration, alpha : 1},
            {duration : this._fadeInDuration, alpha : 0});
    }

    private playPump():void {
        this._isPumping = true;
        this.loopPumping(1);
    }

    private loopPumping(direction:number):void {
        if(!this._isPumping) {
            return;
        }
        const startScale = direction === 1 ? 0.5 : 1;
        const endScale = direction === 1 ? 1 : 0.5;
        const startAlpha = direction === 1 ? 0 : 1;
        const endAlpha = direction === 1 ? 1 : 0;
        const ease = direction === 1 ? "power4.out" : "power4.in";
        const duration = direction === 1 ? 2.4 : 1;
        this._pumpTl = new TimelineLite();
        this._pumpTl.add([
            gsap.fromTo(this._winX.scale,
                {x : startScale, y : startScale},
                {x : endScale, y : endScale, duration : duration, ease : ease}
            ),
            gsap.fromTo(this._winX,
                {alpha : startAlpha},
                {alpha : endAlpha, duration : duration, ease : ease}
            )
        ], direction === 1 ? 0.4 : 0);
        this._pumpTl.add(() => this.loopPumping(-direction));
    }

    private stopPump():void {
        this._isPumping = false;
        if(this._pumpTl?.isActive()) {
            this._pumpTl.pause();
            this._pumpTl.kill();
        }
        this._pumpTl = null;
        this._winX.scale.set(1);
        this._winX.alpha = 1;
    }

    protected close():TimelineLite {
        const tl = new TimelineLite();
        tl.add([
            gsap.to(this, {duration : 1, alpha : 0}),
            () => {
                this.stopPump();
                this._countUp.reset();
                this._particles.stop();
                TombstoneRipSound.soundPlayer.playTrack({name : TombstoneRipSound.FS_BUTTON});
                TombstoneRipSound.fadeAndStopSound({
                    name : TombstoneRipSound.WIN_CAP,
                    to : 0,
                    duration : 300
                });
                TombstoneRipSound.switchMusic(
                    {
                        name : TombstoneRipSound.MAIN_GAME_MUSIC,
                        volume : 1
                    },
                    undefined,
                    {name : "", to : 0, duration : 300}
                );
            }
        ]);
        tl.add(() => {
            SlotGame.keypadPlugin.show();
            this.visible = false;
            this.alpha = 1;
            this._continueButton.visible = false;
            this._slide.currentSprite.visible = false;
        });

        return tl;
    }

    private reset():void {
        this._winX.visible = false;
        this._played.visible = false;
        this._countUp.alpha = 1;
    }
}